import AnnualEventSessions from "../../../components/AnnualEventSessions";
import {eventLinkProperties} from '../../../services/embedded-events';
import React, { useState, useMemo, useEffect } from "react";
import { Calendar, ChevronRight, Lock } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import AiContentSlot from "../../../components/AiContentSlot";
import { applyLang } from "../../../i18n";
import { useEvents } from "../../../services/EventService";
import { formatEventDate } from "../../../components/eventDate";

export default function EventMultiSession({ context }) {
    const { org, lang } = context;
    const { eventGroupes } = useEvents(context);
    const groupeId = context.page?.eventId || context.route[1];
    const langValue = lang?.value || lang;

    // Groupes multi-session issus du fetch client des événements, plus de org.eventGroupes.
    const groupe = useMemo(() => {
        const g = (eventGroupes || []).find(x => x.id === groupeId);
        return g ? applyLang(g, lang) : null;
    }, [eventGroupes, groupeId, lang]);

    const sessions = useMemo(() => {
        if (!groupe) return [];
        const now = Date.now();
        return groupe.sessions
            .map(s => applyLang(s, lang))
            .filter(s => {
                const debut = s.dateDebut ? new Date(s.dateDebut).getTime() : 0;
                const fin = new Date(s.dateFin).getTime();
                return now >= debut && now <= fin;
            });
    }, [groupe, lang]);

    const [selectedId, setSelectedId] = useState(null);

    if (!groupe) return null;
    if (groupe.annualEventPage) return <AnnualEventSessions group={groupe} sessions={sessions} context={context}/>;

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-9">
                    <h1 className="mb-3" style={{ fontWeight: 800 }}>
                        {groupe.title || groupe.label}
                    </h1>
                    {groupe.description && (
                        <p className="text-muted mb-4" style={{ fontSize: "1.1rem" }}>
                            {groupe.description}
                        </p>
                    )}

                    {groupe.aiContentBlockId && (
                        <div className="mb-5">
                            <AiContentSlot blockId={groupe.aiContentBlockId} lang={langValue} />
                        </div>
                    )}

                    <div className="card shadow-sm border-0" style={{ borderRadius: 18 }}>
                        <div className="card-body p-4">
                            <div className="text-uppercase text-muted mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.12em", fontWeight: 700 }}>
                                <Trans>Choisissez votre session</Trans>
                            </div>

                            {sessions.length === 0 ? (
                                <div className="text-center text-muted py-4 px-3" style={{ background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}>
                                    <Trans>Aucune session disponible pour le moment.</Trans>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 320, overflowY: "auto" }}>
                                        {sessions.map(sess => {
                                            const isSelected = sess.id === selectedId;
                                            const isAvail = sess.active;
                                            return (
                                                <React.Fragment key={sess.id}>
                                                    <button
                                                        type="button"
                                                        aria-expanded={isSelected}
                                                        disabled={!isAvail}
                                                        // Toggle : reclic sur la session sélectionnée la referme.
                                                        onClick={() => isAvail && setSelectedId(isSelected ? null : sess.id)}
                                                        className="d-flex justify-content-between align-items-center p-3"
                                                        style={{width:"100%", textAlign:"start", font:"inherit",
                                                            border: isSelected ? "2px solid #0d6efd" : "1px solid #e2e8f0",
                                                            borderRadius: 12,
                                                            background: isSelected ? "#eff6ff" : "#fff",
                                                            cursor: isAvail ? "pointer" : "not-allowed",
                                                            opacity: isAvail ? 1 : 0.55,
                                                            transition: "all 0.15s",
                                                        }}
                                                    >
                                                        <span>
                                                            <span style={{ display:"block", fontWeight: 700, color: "#0f172a" }}>
                                                                <Calendar size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
                                                                {formatEventDate(sess, langValue)}
                                                            </span>
                                                            {(sess.title || sess.name) && <span className="text-muted" style={{ fontSize: "0.85rem" }}>{sess.title || sess.name}</span>}
                                                        </span>
                                                        {!isAvail ? (
                                                            <Lock size={16} color="#94a3b8" />
                                                        ) : (
                                                            <span style={{
                                                                display: "inline-flex",
                                                                transition: "transform 0.2s",
                                                                transform: isSelected ? "rotate(90deg)" : "rotate(0deg)",
                                                            }}>
                                                                <ChevronRight size={18} color={isSelected ? "#0d6efd" : "#cbd5e1"} />
                                                            </span>
                                                        )}
                                                    </button>

                                                    {/* Liens inline juste sous la session ouverte plutôt qu'en bas de liste. */}
                                                    {isSelected && (
                                                        <div className="ms-3 mt-1 mb-2">
                                                            {(sess.links || []).length === 0 ? (
                                                                <div className="text-center text-muted py-3 px-3" style={{ background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}>
                                                                    <Trans>Aucune inscription disponible pour cette session.</Trans>
                                                                </div>
                                                            ) : (
                                                                (sess.links || []).map((link, i) => {
                                                                    const tLink = applyLang(link, lang);
                                                                    return (
                                                                        <a
                                                                            key={i}
                                                                            {...eventLinkProperties(tLink.url,context)}
                                                                            className="d-flex justify-content-between align-items-center mb-2 text-white"
                                                                            style={{
                                                                                padding: "14px 18px",
                                                                                borderRadius: 12,
                                                                                background: "#0f172a",
                                                                                textDecoration: "none",
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            <span>{tLink.name}</span>
                                                                            <ChevronRight size={18} />
                                                                        </a>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
